"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
// Adjust import path as needed

import { BNPModal } from "@/components/bnp-ui/overlays/BNPModal";
import {
  PopupIdentifier,
  PopupItem,
} from "./config/app_popup_manager_context.types";

export type AppPopupData = PopupItem[];

interface TriggerOptions {
  duration?: number | null; // Overrides defaultDuration if provided
  overrideActive?: boolean; // Force immediate display if urgent
}

interface AppPopupManagerContextType {
  triggerPopup: (identifier: PopupIdentifier, options?: TriggerOptions) => void;
  closeCurrentPopup: () => void;
  dismissAll: () => void;
  activePopupId: PopupIdentifier | null;
}

const AppPopupManagerContext = createContext<
  AppPopupManagerContextType | undefined
>(undefined);

// 3. Provider Props
interface AppPopupManagerProviderProps {
  children: React.ReactNode;
  popupData: AppPopupData;
}

interface QueuedPopup {
  item: PopupItem;
  duration?: number | null;
}

export const AppPopupManagerProvider: React.FC<
  AppPopupManagerProviderProps
> = ({ children, popupData }) => {
  const [queue, setQueue] = useState<QueuedPopup[]>([]);
  const [currentPopup, setCurrentPopup] = useState<QueuedPopup | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Map popupData for fast lookup
  const popupRegistry = useRef<Map<PopupIdentifier, PopupItem>>(new Map());

  useEffect(() => {
    const map = new Map<PopupIdentifier, PopupItem>();
    popupData.forEach((item) => map.set(item.identifier, item));
    popupRegistry.current = map;
  }, [popupData]);

  // Clear running auto-dismiss timer
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Close current active popup and process next in queue
  const closeCurrentPopup = useCallback(() => {
    clearTimer();
    setCurrentPopup(null);
  }, [clearTimer]);

  // Trigger function consumed by app components
  const triggerPopup = useCallback(
    (identifier: PopupIdentifier, options?: TriggerOptions) => {
      const popupItem = popupRegistry.current.get(identifier);

      if (!popupItem) {
        console.warn(
          `[AppPopupManager] Popup with identifier "${identifier}" not found in pool.`,
        );
        return;
      }

      const queuedItem: QueuedPopup = {
        item: popupItem,
        duration:
          options?.duration !== undefined
            ? options.duration
            : popupItem.defaultDuration,
      };

      if (options?.overrideActive) {
        clearTimer();
        setCurrentPopup(queuedItem);
        return;
      }

      setQueue((prevQueue) => {
        // Prevent duplicate queueing of the exact same popup if already queued
        if (prevQueue.some((q) => q.item.identifier === identifier)) {
          return prevQueue;
        }
        return [...prevQueue, queuedItem];
      });
    },
    [clearTimer],
  );

  // Queue runner: Pops next popup when active one closes
  useEffect(() => {
    if (!currentPopup && queue.length > 0) {
      const [nextPopup, ...remainingQueue] = queue;
      setCurrentPopup(nextPopup);
      setQueue(remainingQueue);
    }
  }, [currentPopup, queue]);

  // Handles auto-dismissal duration logic
  useEffect(() => {
    if (!currentPopup) return;

    const duration = currentPopup.duration;

    if (typeof duration === "number" && duration > 0) {
      timerRef.current = setTimeout(() => {
        closeCurrentPopup();
      }, duration);
    }

    return () => clearTimer();
  }, [currentPopup, closeCurrentPopup, clearTimer]);

  const dismissAll = useCallback(() => {
    clearTimer();
    setQueue([]);
    setCurrentPopup(null);
  }, [clearTimer]);

  return (
    <AppPopupManagerContext.Provider
      value={{
        triggerPopup,
        closeCurrentPopup,
        dismissAll,
        activePopupId: currentPopup?.item.identifier ?? null,
      }}
    >
      {children}

      {/* Shared Modal Host */}
      {currentPopup && (
        <BNPModal
          isOpen={Boolean(currentPopup)}
          onClose={closeCurrentPopup}
          maxWidth={currentPopup.item.maxWidth}
        >
          {currentPopup.item.component}
        </BNPModal>
      )}
    </AppPopupManagerContext.Provider>
  );
};

// 4. Custom Hook
export const useAppPopupManager = () => {
  const context = useContext(AppPopupManagerContext);
  if (!context) {
    throw new Error(
      "useAppPopupManager must be used within an AppPopupManagerProvider",
    );
  }
  return context;
};
